/**
 * @description This file contains the geo data for the current loaded project.
 * This includes, but is not limited to, the gltft octree
 */

import * as THREE from 'three';
import { Box3 } from 'three';

import {
  FindProjectModel,
  GltftScanModel,
  OrthoTileModel,
  TiledModelVersion,
} from '../../../typings/api/skymap/rest/v0/.common';
import { SkyMapAxiosServiceFactory } from '../services/axios/skymap-axios-service-factory';
import { GeodataStoreFactory } from '../stores/geodata-store-factory/geodata-store-factory';
import { OrthoTileStore } from '../stores/geodata-store-factory/ortho-tile-store/ortho-tile-store';
import { PointCloudStore } from '../stores/geodata-store-factory/point-cloud-store/point-cloud-store';
import { ScanStore } from '../stores/geodata-store-factory/scan-store/scan-store';
import { formattedDateTime } from '../utils/dateUtils';
import { isDefined } from '../utils/variables';
import { CogSource } from './geodata/cog-asset/cog-source';
// @ts-ignore
import { getContainerOrigo, TimelineContainer, TimelineManager } from './timeline-manager';
import { OctTree } from './utilities/octTree/octTree';
import { GLTFTDescriptor, parseGLTFTDescriptorToOctTree } from './utilities/octTree/octTreeParser';
import { ViewerEnvironmentInstance } from './viewer-environment';

export interface ScanData {
  scanChunkPath: string;
  scanClearModel?: string;
  gltftDescriptor: GLTFTDescriptor;
  model: any;
  origo: THREE.Vector3;
  rh2000Correction: number;
  octtree: OctTree;
  boundingBox: Box3;
  store: ScanStore;
}

export interface TiledModelData {
  id: string;
  version: TiledModelVersion;
  geoidHeight: number;
  origo: number[];
}

export interface PointcloudData {
  pointCloudStore: PointCloudStore;
  // Same as for scans, but in preparation for point cloud migration
  octtree: OctTree;
}

export interface CesiumPointCloudData {
  id: string;
  geoidHeight: number;
  origo: number[];
  refine: 'REPLACE' | 'ADD';
  /** COPC is a single Cloud Optimized Point Cloud read with range requests, 3DTILES a tile set. */
  format: '3DTILES' | 'COPC';
}

/**
 * The active orthotile. XYZ orthotiles are a z/x/y tile pyramid read through the store; COG
 * orthotiles are one Cloud Optimized GeoTIFF whose header is read up front, so bounds and origo
 * are known without waiting for the ortho scene.
 */
export type OrthotileData =
  | { format: 'XYZ'; model?: OrthoTileModel; store: OrthoTileStore }
  | { format: 'COG'; model: OrthoTileModel; source: CogSource };

export interface ContainerState {
  scan?: ScanData;
  tiledModel?: TiledModelData;
  pointcloud?: PointcloudData;
  cesiumPointCloud?: CesiumPointCloudData;
  orthotile?: OrthotileData;
  geodataId?: string;
  projection?: string;
  projectionName?: string;
}

export interface DetailedTimelineItem {
  id: string;
  number: number;
  dateFormatted: string;
  dateFormattedWithNumber: string;
  comment: string;
  timelineContainer: TimelineContainer;
}

export class ProjectGeodataManager {
  set isSharedView(state: boolean) {
    this._isSharedView = state;
  }
  /**
   *  Singleton implementation
   */
  static instance(): ProjectGeodataManager {
    return ProjectGeodataManager._instance;
  }

  private static _instance: ProjectGeodataManager = new ProjectGeodataManager();
  private sharedViewId: string | undefined;

  // The timeline manager
  private timelineManager: TimelineManager | undefined;

  private geodataState: ContainerState = {};

  // Loading parameters
  private loading = false;
  /** Counts the activations, so the data of one that a later one or project replaced is dropped. */
  private activationCount = 0;
  private project?: FindProjectModel;

  private _isSharedView = false;

  private readonly activeTimelineContainerListeners = new Set<() => void>();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  load(project?: FindProjectModel, sharedViewId?: string) {
    if (!project) {
      return;
    }

    this.project = project;
    this.sharedViewId = sharedViewId;
    // The last project's data, also of an activation that is still loading
    this.activationCount++;
    this.clearState();

    this.timelineManager =
      project.timelineContainers.length > 0
        ? new TimelineManager(project.timelineContainers)
        : undefined;
    this.notifyActiveTimelineContainer();
  }

  /**
   * Returns a list of detailed timeline items sorted by date.
   */
  getDetailedTimelineItems() {
    const containers = ProjectGeodataManager.instance().getTimelineContainers();
    return containers.map((timeline: TimelineContainer, i: number) => {
      const dateString = formattedDateTime(timeline.collectionDate);

      return {
        id: timeline.id,
        number: containers.length - i,
        dateFormatted: dateString,
        dateFormattedWithNumber: `${containers.length - i}: ${dateString}`,
        comment: timeline.comment,
        timelineContainer: timeline,
      } as DetailedTimelineItem;
    });
  }

  getTimelineContainerIdByIndex(timelineContainerIndex: number): string {
    return this.timelineManager!.getList()[timelineContainerIndex].id;
  }

  /**
   *  Activate the timeline container based on the index value.
   *  Then load the related geodata information.
   * @param timelineContainerId
   */
  /**
   * Makes the timeline container the active one without loading its data, as in the CAD viewer,
   * which shows none.
   */
  selectTimelineContainer(timelineContainerId: string) {
    this.timelineManager?.selectById(timelineContainerId);
    this.notifyActiveTimelineContainer();
  }

  /**
   * Activate the timeline container based on the id and load related geodata. Throws an
   * AbortError, leaving the data as it is, when another container is activated or another project
   * loaded before it's done.
   */
  async activateTimelineContainer(timelineContainerId: string) {
    const activation = ++this.activationCount;
    this.clearState();

    if (!isDefined(this.timelineManager)) {
      return;
    }

    const spatialRef = ViewerEnvironmentInstance.getSpatialReference();
    const coordinateSystem =
      spatialRef.projection && spatialRef.projectionName
        ? { proj4: spatialRef.projection, name: spatialRef.projectionName }
        : this.project?.coordinateSystemModel;

    if (!coordinateSystem) {
      throw Error('Project has not coordinate system set.');
    }

    this.selectTimelineContainer(timelineContainerId);
    this.loading = true;

    const [legacyData, cesiumPointCloudData, orthoTileData, tiledModelData] = await Promise.all([
      this.extractLegacyScanAndPointCloudData(),
      this.extractCesiumPointCloudData(),
      this.extractOrthotileData(),
      this.extractTiledModelData(),
    ]);
    if (activation !== this.activationCount) {
      throw new DOMException('Another timeline container was activated.', 'AbortError');
    }

    this.geodataState = {
      scan: legacyData.scan,
      tiledModel: tiledModelData,
      pointcloud: legacyData.pointCloud,
      cesiumPointCloud: cesiumPointCloudData,
      orthotile: orthoTileData,
      // Consider getting the geodatas coordinate system here instead.
      // By using the projects projection instead of the active geodatas projection
      // It is possible to have a geodata processed with one coordinate system
      // and then convert it with another coordinate system when loading into the viewer.
      projection: coordinateSystem.proj4,
      projectionName: coordinateSystem.name,
    };

    this.geodataState.geodataId = this.extractGeodataId();

    this.loading = false;

    return this.timelineManager.current();
  }

  setGeodataState(state: ContainerState) {
    this.geodataState = state;
  }

  /**
   *  Get the project
   */
  getProject(): FindProjectModel | undefined {
    return this.project;
  }

  /**
   *  Returns if project geodata is loading
   */
  isLoading(): boolean {
    return this.loading;
  }

  /**
   *  Get the active timeline geodata
   */
  getActiveScanData(): ScanData | undefined {
    return this.geodataState.scan;
  }
  getActiveTiledModelData(): TiledModelData | undefined {
    return this.geodataState.tiledModel;
  }
  getActivePointcloudGeodata(): PointcloudData | undefined {
    return this.geodataState.pointcloud;
  }
  getActiveCesiumPointCloudGeodata(): CesiumPointCloudData | undefined {
    return this.geodataState.cesiumPointCloud;
  }
  getActivOrthotileGeodata(): OrthotileData | undefined {
    return this.geodataState.orthotile;
  }
  getActiveGeodataId(): string | undefined {
    return this.geodataState.geodataId;
  }
  getActiveTimelineContainer(): TimelineContainer | undefined {
    return this.timelineManager && this.timelineManager.current();
  }

  /**
   * Calls the listener when the active timeline container may have changed: when a project is
   * loaded, and when a container is activated, before its data has loaded. For
   * useSyncExternalStore. Returns a function that unsubscribes.
   */
  subscribeToActiveTimelineContainer = (listener: () => void) => {
    this.activeTimelineContainerListeners.add(listener);
    return () => {
      this.activeTimelineContainerListeners.delete(listener);
    };
  };

  private notifyActiveTimelineContainer() {
    this.activeTimelineContainerListeners.forEach((listener) => listener());
  }

  /**
   *  Return the project timeline containers
   */
  getTimelineContainers(): TimelineContainer[] {
    if (!isDefined(this.timelineManager)) {
      return [];
    }

    const containers = this.timelineManager.getList();
    return containers.map((x, i) => {
      x.containerIndex = containers.length - i;
      return x;
    });
  }

  /**
   *  Returns if the project has geodata
   */
  hasScanData(): boolean {
    return !!this.geodataState.scan || !!this.geodataState.tiledModel;
  }
  hasTiledModelData(): boolean {
    return !!this.geodataState.tiledModel;
  }
  hasPointcloudData(): boolean {
    return !!this.geodataState.pointcloud;
  }
  hasCesiumPointCloudData(): boolean {
    return !!this.geodataState.cesiumPointCloud;
  }
  hasOrthotileData(): boolean {
    return !!this.geodataState.orthotile;
  }
  hasGeodata(): boolean {
    return !!this.geodataState.geodataId;
  }
  hasTimelineGeodata(): boolean {
    // Check that there is atleast one valid timeline container
    return !!this.timelineManager && this.timelineManager.getLatestContainer() !== undefined;
  }

  /**
   * Returns the origo of the timeline container's data, or, for a container without data, of the
   * container with data closest in time, so it opens where the data is. None if neither has one.
   */
  resolveTimelineContainerOrigo(timelineContainer: TimelineContainer): THREE.Vector3 | undefined {
    const closest = this.timelineManager?.getClosestContainerWithData(timelineContainer);
    return closest && getContainerOrigo(closest);
  }

  /**
   *  Returns the available geodata of a timeline container.
   *  If no id is supplied, return the current available.
   * @param timelineContainerId
   */
  resolveAvailableContainerGeodata(timelineContainerId?: string) {
    const timelineContainer = timelineContainerId
      ? this.timelineManager!.getContainerById(timelineContainerId)
      : this.timelineManager!.current();

    return {
      hasScan: isDefined(timelineContainer?.scan) || isDefined(timelineContainer?.tiledModelId),
      hasOrthoTile: !!(timelineContainer && timelineContainer.orthotile),
      hasPointCloud: !!(timelineContainer && timelineContainer.pointCloud),
    };
  }

  /**
   *  Returns if MAS is available
   */
  isMasAvailable() {
    return this.hasScanData() || this.hasCesiumPointCloudData();
  }

  /**
   *  Get projects projection
   */
  getProjectProjection(): string | undefined {
    return this.geodataState.projection;
  }

  hasCoordinateSystem(): boolean {
    const projection = this.geodataState.projection;
    return !!projection && projection !== 'local';
  }

  /**
   *  Get projects projection name
   */
  getProjectionName(): string {
    return this.geodataState.projectionName ?? 'Lokala koordinater';
  }

  /**
   *  Clear the loaded geodata state
   */
  clearState() {
    this.geodataState = {};
    this.loading = false;
  }

  getClearModel = (child: any): string | undefined => {
    if (!child) {
      return undefined;
    }

    if (!child.geometry) {
      return this.getClearModel(child.children[0]);
    }

    return child.geometry;
  };

  /**
   *  Extract the point cloud id from the timeline container
   * @param id
   */
  async extractCesiumPointCloudData(id?: string): Promise<CesiumPointCloudData | undefined> {
    const timelineContainer = id
      ? this.timelineManager?.getContainerById(id)
      : this.timelineManager?.current();

    if (!timelineContainer?.pointCloud) {
      return undefined;
    }

    if (!timelineContainer?.pointCloudObject) {
      timelineContainer.pointCloudObject = await this.getPointCloud(timelineContainer.pointCloud);
    }

    return timelineContainer.pointCloudObject;
  }

  async getPointCloud(pointCloudId: string) {
    const pointCloudService = SkyMapAxiosServiceFactory.instance.createPointCloudServiceV0();
    const sharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();
    const response = this.sharedViewId
      ? await sharedViewService.getPointCloud({ path: { id: this.sharedViewId, pointCloudId } })
      : await pointCloudService.fetchById({ path: { id: pointCloudId } });

    return response.data;
  }

  private async loadTiledModel(tiledModelId?: string) {
    if (!isDefined(tiledModelId)) {
      return;
    }

    if (isDefined(this.sharedViewId)) {
      const sharedViewAxiosService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();
      return (
        await sharedViewAxiosService.getTiledModel({
          path: { id: this.sharedViewId, tiledModelId },
        })
      ).data;
    }
    const tiledModelAxiosService = SkyMapAxiosServiceFactory.instance.createTiledModelServiceV1();
    return (
      await tiledModelAxiosService.getTiledModel({
        path: { tiledModelId },
      })
    ).data;
  }

  /**
   *  Load the active timeline container scan data
   */
  private async extractLegacyScanAndPointCloudData(): Promise<{
    scan?: ScanData;
    pointCloud?: PointcloudData;
  }> {
    const timelineContainer = this.timelineManager!.current();
    if (!timelineContainer?.scan) {
      return {};
    }

    if (!timelineContainer.scadData) {
      const scanService = SkyMapAxiosServiceFactory.instance.createScanServiceV0();
      const activeScan: GltftScanModel | undefined = (
        await scanService.getProject({ body: { id: timelineContainer.scan } })
      ).data;

      if (!activeScan) {
        return {};
      }

      const origo = new THREE.Vector3();
      let rh2000Correction = 0;
      if (activeScan.coordinates) {
        const scanOrigo = activeScan.coordinates.origo.slice(0);
        origo.set(scanOrigo[0], scanOrigo[1], scanOrigo[2]);

        // Since tiled archive export from agisoft uses wgs84 + egm96 geoid, add RH2000Correction to compensate for the diff between rh2000 and egm96.
        rh2000Correction = activeScan.rh2000Correction;
      }

      const store = await GeodataStoreFactory.instance.createScanStore(activeScan.id);
      const gltftDescriptor = await store.fetchDescriptor();
      const octtree = parseGLTFTDescriptorToOctTree(gltftDescriptor);

      timelineContainer.scadData = {
        model: activeScan,
        gltftDescriptor,
        origo,
        rh2000Correction,
        scanChunkPath: gltftDescriptor.root.geometry,
        scanClearModel: this.getClearModel(gltftDescriptor.root.children[0]),
        octtree,
        boundingBox: octtree.rootNode.boundingBox.clone(),
        store,
      };

      if (activeScan.pointcloud) {
        const pointCloudStore = await GeodataStoreFactory.instance.createPointCloudStore(
          activeScan.id,
          activeScan.pointcloud,
        );

        timelineContainer.legacyPointCloudData = {
          pointCloudStore,
          octtree: parseGLTFTDescriptorToOctTree(await pointCloudStore.fetchDescriptor()),
        };
      }
    }

    return {
      scan: timelineContainer.scadData,
      pointCloud: timelineContainer.legacyPointCloudData,
    };
  }

  private async extractTiledModelData(): Promise<TiledModelData | undefined> {
    const currentContainer = this.timelineManager!.current();

    if (!currentContainer?.tiledModelId) {
      return undefined;
    }

    if (!currentContainer?.tiledModelObject) {
      currentContainer.tiledModelObject = await this.loadTiledModel(currentContainer.tiledModelId);
    }

    if (!currentContainer.tiledModelObject) {
      return;
    }

    return {
      id: currentContainer.tiledModelObject.id,
      origo: currentContainer.tiledModelObject.origo,
      version: currentContainer.tiledModelObject.version,
      geoidHeight: currentContainer.tiledModelObject.geoidHeight,
    };
  }

  /**
   *  Load the active timeline container orthotile data
   */
  private async extractOrthotileData(): Promise<OrthotileData | undefined> {
    const timelineContainer = this.timelineManager!.current();

    if (!timelineContainer?.orthotile) {
      return;
    }

    if (!timelineContainer.orthoTileData) {
      const orthoTileService = SkyMapAxiosServiceFactory.instance.createOrthoTileServiceV0();
      const sharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();
      const result = this.sharedViewId
        ? await sharedViewService.getOrthoTile({
            path: { id: this.sharedViewId, orthoTileId: timelineContainer.orthotile },
          })
        : await orthoTileService.fetchById({
            body: { id: timelineContainer.orthotile },
          });
      const model = result.data;

      timelineContainer.orthoTileData =
        model.format === 'COG'
          ? await this.openCogOrthotile(timelineContainer.orthotile, model)
          : {
              format: 'XYZ',
              model,
              store: await GeodataStoreFactory.instance.createOrthoTileStore(
                timelineContainer.orthotile,
              ),
            };
    }

    return timelineContainer.orthoTileData;
  }

  /**
   * Reads the COG's header. A broken or missing file leaves the project without an orthophoto
   * instead of failing the whole load, like missing XYZ tiles would.
   */
  private async openCogOrthotile(
    orthoTileId: string,
    model: OrthoTileModel,
  ): Promise<OrthotileData | undefined> {
    try {
      const url = await GeodataStoreFactory.instance.createOrthoTileCogUrl(orthoTileId);
      return { format: 'COG', model, source: await CogSource.open(url) };
    } catch (err) {
      console.error(`Failed to open COG orthotile ${orthoTileId}:`, err);
      return undefined;
    }
  }

  /**
   *  Load the active timeline container geodata
   */
  private extractGeodataId(): string | undefined {
    const orthotileModel = this.geodataState.orthotile?.model;
    const scanModel = this.geodataState.scan?.model;
    const geodataId = orthotileModel?.parentGeodataId ?? scanModel?.parentGeodataId;

    if (!geodataId || this._isSharedView) {
      return undefined;
    }

    return geodataId;
  }
}
